import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  useToast,
} from '@caruso/ui';
import { Save, Palette, RotateCcw, Eye } from 'lucide-react';
import { brandingService, BrandingConfig, defaultBranding } from '@/services/branding.service';
import { useTheme } from '@/contexts/theme-context';
import { cn } from '@caruso/ui';

interface ColorInputProps {
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  defaultValue: string;
}

function ColorInput({ label, description, value, onChange, defaultValue }: ColorInputProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-brand-ivory">{label}</Label>
          <p className="text-xs text-brand-ivory/50">{description}</p>
        </div>
        <button
          type="button"
          onClick={() => onChange(defaultValue)}
          className="text-xs text-brand-gold hover:underline"
          title="Restaurar padrao"
        >
          <RotateCcw className="w-3 h-3" />
        </button>
      </div>
      <div className="flex gap-2">
        <div
          className="w-11 h-11 rounded-lg border border-brand-gold/20 cursor-pointer overflow-hidden"
          style={{ backgroundColor: value }}
        >
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full h-full opacity-0 cursor-pointer"
          />
        </div>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory uppercase font-mono"
          maxLength={7}
        />
      </div>
    </div>
  );
}

export function BrandingPage() {
  const { toast } = useToast();
  const { refetch: refetchTheme } = useTheme();

  const [colors, setColors] = useState({
    primaryColor: defaultBranding.primaryColor,
    secondaryColor: defaultBranding.secondaryColor,
    accentColor: defaultBranding.accentColor,
    backgroundColor: defaultBranding.backgroundColor,
    surfaceColor: defaultBranding.surfaceColor,
    textColor: defaultBranding.textColor,
  });

  const [appName, setAppName] = useState(defaultBranding.appName);

  const { data: branding, isLoading } = useQuery({
    queryKey: ['branding-settings'],
    queryFn: () => brandingService.get(),
  });

  // Update local state when branding is loaded
  useEffect(() => {
    if (branding) {
      setColors({
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor,
        accentColor: branding.accentColor,
        backgroundColor: branding.backgroundColor,
        surfaceColor: branding.surfaceColor,
        textColor: branding.textColor,
      });
      setAppName(branding.appName);
    }
  }, [branding]);

  const mutation = useMutation({
    mutationFn: async (data: Partial<BrandingConfig>) => {
      return brandingService.update(data);
    },
    onSuccess: () => {
      toast({
        title: 'Cores atualizadas',
        description: 'As cores do tema foram salvas com sucesso!',
        variant: 'success',
      });
      // Refresh theme
      refetchTheme();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao salvar',
        description: error.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    },
  });

  const handleSave = () => {
    mutation.mutate({
      ...colors,
      appName,
    });
  };

  const handleResetAll = () => {
    setColors({
      primaryColor: defaultBranding.primaryColor,
      secondaryColor: defaultBranding.secondaryColor,
      accentColor: defaultBranding.accentColor,
      backgroundColor: defaultBranding.backgroundColor,
      surfaceColor: defaultBranding.surfaceColor,
      textColor: defaultBranding.textColor,
    });
    setAppName(defaultBranding.appName);
  };

  const updateColor = (key: keyof typeof colors) => (value: string) => {
    setColors((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return <div className="text-center py-12 text-brand-ivory/50">Carregando...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Personalizar Cores</h1>
          <p className="text-brand-ivory/60 mt-2">
            Configure as cores do tema da sua empresa
          </p>
        </div>
        <Button variant="ghost" onClick={handleResetAll} className="text-brand-ivory/70">
          <RotateCcw className="w-4 h-4 mr-2" />
          Restaurar Padrao
        </Button>
      </div>

      {/* Preview Card */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-brand-gold/10">
              <Eye className="w-5 h-5 text-brand-gold" />
            </div>
            <div>
              <CardTitle className="text-brand-gold">Pre-visualizacao</CardTitle>
              <CardDescription className="text-brand-ivory/60">
                Veja como ficara o tema com as cores selecionadas
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div
            className="rounded-xl p-6 space-y-4"
            style={{ backgroundColor: colors.backgroundColor }}
          >
            <div
              className="rounded-lg p-4"
              style={{ backgroundColor: colors.surfaceColor }}
            >
              <h3
                className="font-serif text-lg mb-2"
                style={{ color: colors.primaryColor }}
              >
                {appName}
              </h3>
              <p style={{ color: colors.textColor }}>
                Este e um exemplo de texto no tema personalizado.
              </p>
              <div className="flex gap-2 mt-4">
                <button
                  className="px-4 py-2 rounded-lg font-medium"
                  style={{
                    backgroundColor: colors.primaryColor,
                    color: colors.backgroundColor,
                  }}
                >
                  Botao Primario
                </button>
                <button
                  className="px-4 py-2 rounded-lg font-medium"
                  style={{
                    backgroundColor: colors.secondaryColor,
                    color: colors.textColor,
                  }}
                >
                  Botao Secundario
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Color Settings */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-brand-gold/10">
              <Palette className="w-5 h-5 text-brand-gold" />
            </div>
            <div>
              <CardTitle className="text-brand-gold">Cores do Tema</CardTitle>
              <CardDescription className="text-brand-ivory/60">
                Defina as 4 cores principais do seu tema
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ColorInput
              label="Cor Primaria (Destaque)"
              description="Usada em botoes, links e elementos de destaque"
              value={colors.primaryColor}
              onChange={updateColor('primaryColor')}
              defaultValue={defaultBranding.primaryColor}
            />

            <ColorInput
              label="Cor de Fundo"
              description="Fundo principal da aplicacao"
              value={colors.backgroundColor}
              onChange={updateColor('backgroundColor')}
              defaultValue={defaultBranding.backgroundColor}
            />

            <ColorInput
              label="Cor de Superficie"
              description="Cards, inputs, modais"
              value={colors.surfaceColor}
              onChange={updateColor('surfaceColor')}
              defaultValue={defaultBranding.surfaceColor}
            />

            <ColorInput
              label="Cor do Texto"
              description="Texto principal da aplicacao"
              value={colors.textColor}
              onChange={updateColor('textColor')}
              defaultValue={defaultBranding.textColor}
            />
          </div>

          <hr className="border-brand-gold/10" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ColorInput
              label="Cor Secundaria"
              description="Elementos secundarios e complementares"
              value={colors.secondaryColor}
              onChange={updateColor('secondaryColor')}
              defaultValue={defaultBranding.secondaryColor}
            />

            <ColorInput
              label="Cor de Acento"
              description="Texto claro alternativo"
              value={colors.accentColor}
              onChange={updateColor('accentColor')}
              defaultValue={defaultBranding.accentColor}
            />
          </div>
        </CardContent>
      </Card>

      {/* App Name */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <CardHeader>
          <CardTitle className="text-brand-gold">Nome da Aplicacao</CardTitle>
          <CardDescription className="text-brand-ivory/60">
            Este nome aparecera no titulo da pagina
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            value={appName}
            onChange={(e) => setAppName(e.target.value)}
            placeholder="Nome da sua empresa"
            className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-4 justify-end">
        <Button type="button" variant="premium" onClick={handleSave} disabled={mutation.isPending}>
          <Save className="w-4 h-4 mr-2" />
          {mutation.isPending ? 'Salvando...' : 'Salvar Cores'}
        </Button>
      </div>
    </div>
  );
}
